/**
 * useHouseholdLiveSync
 *
 * Subscribes to live Firestore updates for shared surfaces so a partner's
 * edits appear within seconds without a manual refresh.
 *
 * Covers grocery list, meal plan and recipe metadata, plus a watch on the
 * household document itself so being removed by a partner is noticed. Inventory
 * has its own live subscription via subscribeToInventoryChanges; preferences
 * are still deferred.
 *
 * Recipe metadata used to be deferred too, on the theory that it "writes
 * through fast enough". It does not: the meal plan is live, so a partner's
 * COOKED stamp appeared within seconds while the verdicts behind it needed a
 * full app reload — a cooked card with nobody's answer on it. Anything two
 * people can both touch needs a listener, not a one-shot download.
 *
 * Subscriptions activate whenever the user is signed in (paired or solo)
 * and tear down on sign-out / unpair.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch, store } from '../redux/store';
import { flushPendingRecipeMetadataSync } from '../redux/recipeMetadataSyncMiddleware';
import { flushPendingCookLogSync } from '../redux/cookLogSyncMiddleware';
import {
  hydrateGroceryListFromCloud,
} from '../redux/groceryListSlice';
import {
  mergeMealPlanFromLiveSync,
  rehydrateMissingRecipes,
  PlannedMeal,
} from '../redux/mealPlanSlice';
import { hydrateMetadataFromCloud } from '../redux/recipeMetadataSlice';
import { subscribeToGroceryList } from '../services/groceryListSyncService';
import { subscribeToMealPlanWeeks } from '../services/mealPlanSyncService';
import { subscribeToRecipeMetadata } from '../services/recipeMetadataSyncService';
import {
  publishMemberProfile,
  subscribeToHousehold,
  getUserHouseholdId,
} from '../services/householdService';
import { isFirebaseEnabled, auth } from '../services/firebaseConfig';
import { useHouseholdExit } from './useHouseholdExit';

export const useHouseholdLiveSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const uid = useSelector((s: RootState) => s.auth?.user?.uid ?? null);
  const householdId = useSelector(
    (s: RootState) => s.auth?.householdId ?? null
  );
  const { returnToSoloScope } = useHouseholdExit();

  // Re-subscribe whenever uid OR householdId changes (sign-in, sign-out,
  // pairing, unpairing). The dependency on householdId ensures we flip to
  // the household path the moment the user joins one.
  useEffect(() => {
    if (!isFirebaseEnabled() || !uid) return;

    // Drain the recipe-metadata outbox first. Anything in it is a rating or a
    // note the cloud was never told about — a tap that landed before auth
    // resolved, or a write the tab died in the middle of. This effect runs
    // exactly when the two things that write needs are finally known: the uid
    // and the scope. Before the outbox existed those taps were simply gone.
    void flushPendingRecipeMetadataSync(store.getState, dispatch);
    // Same for the cook journal, where cook notes are kept (#795).
    void flushPendingCookLogSync(store.getState, dispatch);

    const unsubGrocery = subscribeToGroceryList(uid, householdId, (data) => {
      if (!data) return;
      dispatch(
        hydrateGroceryListFromCloud({
          items: data.items,
          customItems: data.customItems,
          generatedDate: data.generatedDate,
          weekSnapshot: data.weekSnapshot,
          sourceWeekStart: data.sourceWeekStart,
          coversUntil: data.coversUntil,
        })
      );
    });

    const unsubMealPlan = subscribeToMealPlanWeeks(
      uid,
      householdId,
      (cloudWeeks) => {
        const weeks: Record<string, PlannedMeal[]> = {};
        for (const [weekStart, week] of Object.entries(cloudWeeks)) {
          weeks[weekStart] = week.meals as unknown as PlannedMeal[];
        }
        dispatch(mergeMealPlanFromLiveSync({ weeks }));
        if (uid) dispatch(rehydrateMissingRecipes(uid));
      }
    );

    // Ratings and verdicts. The reducer merges per field rather than letting
    // the cloud win wholesale, so a snapshot that lands mid-answer cannot
    // revert a verdict that has not round-tripped yet.
    const unsubMetadata = subscribeToRecipeMetadata(
      uid,
      householdId,
      (metadata) => {
        dispatch(hydrateMetadataFromCloud(metadata));
      }
    );

    // Publish my identity into the household roster so my partner's cards can
    // render my initial. Fire-and-forget: a failure costs an avatar letter,
    // never a sync.
    //
    // The email must be passed. This runs moments after the join endpoint has
    // already written a complete roster entry, so omitting it does not leave
    // the field unset — it overwrites the server's value with null, and the
    // partner's screen falls back from a name to nothing.
    if (householdId) {
      publishMemberProfile(
        householdId,
        uid,
        auth?.currentUser?.displayName ?? null,
        auth?.currentUser?.email ?? null
      ).catch(() => {});
    }

    // Being removed by your partner (#604) has no notification channel of its
    // own, and it cannot have one: `households/{id}` is readable only by its
    // members, so the moment the removal lands this app stops being told
    // anything at all. What it gets instead is a permission-denied on the
    // listener — which is the signal.
    //
    // Acted on only after re-reading `users/{uid}.householdId`, which the
    // owner can always read. A denial can also come from a transient auth
    // state, and flipping a still-paired user to personal scope would strand
    // everything they wrote until the next reload. The profile document is the
    // record of truth; the listener merely says "go and check".
    //
    // Mounted here rather than in useHouseholdMembers because this hook is
    // mounted exactly once, in app/(app)/_layout.tsx. That hook runs on every
    // screen that shows a roster.
    const unsubHousehold = householdId
      ? subscribeToHousehold(
          householdId,
          () => {},
          () => {
            getUserHouseholdId(uid)
              .then((stillPaired) => {
                if (!stillPaired || stillPaired !== householdId) {
                  return returnToSoloScope();
                }
                return undefined;
              })
              .catch(() => {});
          }
        )
      : () => {};

    return () => {
      unsubGrocery();
      unsubMealPlan();
      unsubMetadata();
      unsubHousehold();
    };
  }, [uid, householdId, dispatch, returnToSoloScope]);
};
